import {
  Box,
  CircularProgress,
  Grid2 as Grid,
  useMediaQuery,
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Alert,
  Avatar,
  Chip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  changeUserSmsConsent,
  fetchUserData,
} from "../../store/actions/userActions";
import {
  changeSubUserSmsConsent,
  fetchSubUserData,
} from "../../store/actions/subUserActions";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import LottieAnimation from "../../animation/LottieAnimation";
import { useEffect, useState, useRef } from "react";
import { useTheme } from "@emotion/react";
import { useDispatch, useSelector } from "react-redux";
import ChangeFA from "../ChangeFA";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NotaryIcon from "@mui/icons-material/AccountBalance";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BadgeIcon from "@mui/icons-material/Badge";

import { fetchPrivateProviderData } from "../../store/actions/privateProviderActions";
import LicenseInfoSection from "../privateProvider/LicenseInfoSection";
import NotaryUserPaymentModal from "../modals/NotaryUserPaymentModal";
import NotaryCancelModal from "../modals/NotaryCancelModal";
import { timezoneMappings } from "../../data/timezones";
import TimezoneSelect from "../layout/TimezoneSelect";

const ProfileSettings = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  //? SCREEN SIZES ##################################################################
  const mobileSize = useMediaQuery(theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery(theme.breakpoints.down("md"));
  const largePC = useMediaQuery(theme.breakpoints.up("xl"));
  //? ###############################################################################

  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession].user);
  const isEnterprise = Boolean(currentUser?.user?.enterprise?.is_enterprise);
  const isBranch = Boolean(currentUser?.user?.enterprise?.is_branch);
  const canSeeNotaryUserSetup = !currentUser?.subuser && !isEnterprise;
  const canSeeCancelSubscription =
    !currentUser?.subuser && !isEnterprise && !isBranch;
  const notarySetupDescription = isBranch
    ? "Set up your account as a Notary User to provide notarization services."
    : "Set up your account as a Notary User to provide notarization services. This feature is available for Premium and Rockstar plans.";
  const notaryNameMatchText = isBranch
    ? "Your notary account name must match your Branch Manager name"
    : "Your notary account name must match your Permit Rockstar profile name";

  const [dataLoading, setDataLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorTextPassword, setErrorTextPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [qrCodeSrc, setQRCodeSrc] = useState(null);
  const [currentFA, setCurrentFA] = useState("");
  const [errorTextCancel, setErrorTextCancel] = useState("");
  const [imageList, setImageList] = useState([]);
  const [currentImg, setCurrentImg] = useState("");
  const [imgWidth, setImgWidth] = useState("");
  const [leaveInput, setLeaveInput] = useState("");
  const [cancelSelected, setCancelSelected] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [leavingReasons, setLeavingReasons] = useState([]);

  const [smsConsent, setSmsConsent] = useState(false);
  const [authType, setAuthType] = useState("");
  const [switchLoading, setSwitchLoading] = useState(false);
  const [timezone, setTimezone] = useState("America/New_York");

  // Notary user setup states
  const [notaryAccordionExpanded, setNotaryAccordionExpanded] = useState(false);
  const [notaryError, setNotaryError] = useState("");
  const [notarySuccess, setNotarySuccess] = useState("");
  const [commissionNumber, setCommissionNumber] = useState("");
  const [notaryFieldErrors, setNotaryFieldErrors] = useState({});

  // Notary payment modal state
  const [notaryPaymentModalOpen, setNotaryPaymentModalOpen] = useState(false);
  const [notaryUserData, setNotaryUserData] = useState(null);

  const [notaryCancelModalOpen, setNotaryCancelModalOpen] = useState(false);
  const [notaryCancelLoading, setNotaryCancelLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [convertedNotaryUser, setConvertedNotaryUser] = useState(null);

  const qcountRef = useRef(0);
  const dataCountRef = useRef(0);

  useEffect(() => {
    if (currentUser?.subuser) {
      setSmsConsent(currentUser?.subuser?.smsConsent);
      setAuthType(currentUser?.subuser?.auth_type);
    } else {
      setSmsConsent(currentUser?.user?.smsConsent);
      setAuthType(currentUser?.user?.auth_type);
    }
  }, [currentUser]);

  useEffect(() => {
    const isTimezoneInList = (tz) =>
      timezoneMappings.some((timezoneOption) => timezoneOption.value === tz);

    if (currentUser?.subuser?.timezone) {
      setTimezone(
        isTimezoneInList(currentUser?.subuser?.timezone)
          ? currentUser?.subuser?.timezone
          : "America/New_York",
      );
      return;
    }

    const mainUserTimezone = currentUser?.user?.personal?.[0]?.timezone;
    setTimezone(
      isTimezoneInList(mainUserTimezone)
        ? mainUserTimezone
        : "America/New_York",
    );
  }, [currentUser]);

  useEffect(() => {
    if (currentUser?.user?.notary_user) {
      setConvertedNotaryUser(currentUser?.user?.notary_user);
    }
  }, [currentUser]);

  useEffect(() => {
    const getLeavingReasons = async () => {
      const dataResp = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/nocApi/data/leaving-reasons`,
      );

      setLeavingReasons(dataResp.data.data.reasons);
      setDataLoading(false);
    };

    if (dataCountRef.current < 1) {
      getLeavingReasons();
      dataCountRef.current += 1;
    }
  }, []);

  useEffect(() => {
    if (errorTextPassword !== "") {
      const passwordTimer = setTimeout(() => setErrorTextPassword(""), 3000);
      return () => clearTimeout(passwordTimer);
    }

    if (errorTextCancel !== "") {
      const cancelTimer = setTimeout(() => setErrorTextCancel(""), 3000);
      return () => clearTimeout(cancelTimer);
    }
  }, [errorTextPassword, errorTextCancel, currentSession]);

  useEffect(() => {
    const getQr = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/auth/2fa/generate_to_change_qr`,
          { currentSession: currentSession },
          {
            responseType: "blob",
            withCredentials: true,
          },
        );
        if (response.status === 200) {
          const qrCodeUrl = URL.createObjectURL(new Blob([response.data]));
          setQRCodeSrc(qrCodeUrl);
        }
      } catch (e) {}
    };

    if (qcountRef.current < 1) {
      qcountRef.current += 1;
      getQr();
    }
  }, [currentSession]);

  useEffect(() => {
    setImageList([
      {
        alt: "email",
        src: "/img/logos/emailImage.png",
        // width: 120,
        width: 80,
      },
      {
        alt: "mobile",
        src: "/img/logos/phone.png",
        // width: 180,
        width: 120,
      },

      {
        alt: "authenticator",
        src: "/img/logos/authenticator.png",
        // width: 167,
        width: 110,
      },
    ]);

    if (currentUser?.subuser) {
      if (currentUser?.subuser.auth_type === "totp") {
        setCurrentFA("totp");
        setCurrentImg("/img/logos/authenticator.png");
        setImgWidth(127);
      } else if (currentUser?.subuser.auth_type === "emailFa") {
        setCurrentFA("emailFa");
        setCurrentImg("/img/logos/emailImage.png");
        setImgWidth(80);
      } else if (currentUser?.subuser.auth_type === "smsFa") {
        setCurrentFA("smsFa");
        setImgWidth(140);
        setCurrentImg("/img/logos/phone.png");
      }
    } else {
      if (currentUser?.user.auth_type === "totp") {
        setCurrentFA("totp");
        setCurrentImg("/img/logos/authenticator.png");
        setImgWidth(127);
      } else if (currentUser?.user.auth_type === "emailFa") {
        setCurrentFA("emailFa");
        setCurrentImg("/img/logos/emailImage.png");
        setImgWidth(80);
      } else if (currentUser?.user.auth_type === "smsFa") {
        setCurrentFA("smsFa");
        setImgWidth(140);
        setCurrentImg("/img/logos/phone.png");
      }
    }
  }, [currentUser]);

  // Notary user helper functions
  const getNotaryUserLimit = () => {
    // Admin users get 10 notary users
    if (currentUser?.user?.role === "admin") {
      return 10;
    }

    const limit = currentUser?.service_tiers?.notary_user?.limit || 0;

    if (typeof limit === "string" && limit?.toLowerCase() === "unlimited") {
      return 99999999;
    }

    return Number(currentUser?.service_tiers?.notary_user?.limit || 0);
  };

  const getCurrentNotaryUserCount = () => {
    let subUsersNotaryCount = 0;

    if (!currentUser?.subusers_row) {
      subUsersNotaryCount = 0;
    } else {
      subUsersNotaryCount = currentUser.subusers_row.filter(
        (subuser) => subuser.notary_user?.is_notary,
      ).length;
    }

    // Check if main user is a notary by looking at plan.notary.subscriptions
    const mainUserNotaryCount = isMainUserAlreadyNotary() ? 1 : 0;

    return subUsersNotaryCount + mainUserNotaryCount;
  };

  const canAddNotaryUser = () => {
    const limit = getNotaryUserLimit();
    const currentCount = getCurrentNotaryUserCount();

    if (limit === "Unlimited") return true;
    return currentCount < limit;
  };

  const isMainUserAlreadyNotary = () => {
    // Check tier.notary.subscriptions for any main-user notary subscription
    // Main user subscription has is_subuser: false and both subscription_id and user_id present
    return (
      convertedNotaryUser &&
      convertedNotaryUser?.on_site_registered &&
      convertedNotaryUser?.bn_registered
    );
  };

  const canSetupMainUserAsNotary = () => {
    // Check if user is main user (not subuser)
    if (currentUser?.subuser) return false;

    // Check if Free plan
    if (currentUser?.tier?.title === "Free") return false;

    // Check if already a notary
    if (isMainUserAlreadyNotary()) return false;

    // Check notary user limits
    return canAddNotaryUser();
  };

  const validateNotaryFields = () => {
    const errors = {};

    if (!commissionNumber || commissionNumber.trim().length === 0) {
      errors.commissionNumber = "Commission number is required";
    }

    setNotaryFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNotarySetup = async () => {
    if (!validateNotaryFields()) {
      return;
    }

    // Prepare notary user data for the payment modal
    // This will trigger a search in the NotaryUserPaymentModal
    const notaryData = {
      first: currentUser?.user?.personal?.[0]?.firstName || "",
      last: currentUser?.user?.personal?.[0]?.lastName || "",
      isConversion: true,
      isMainUser: true, // This is for main user setup
      commission_number: commissionNumber.trim(),
    };

    setNotaryUserData(notaryData);
    setNotaryPaymentModalOpen(true);
  };

  const handleNotaryPaymentSuccess = (message) => {
    setNotarySuccess(message);
    setCommissionNumber("");
    setNotaryFieldErrors({});
    setNotaryAccordionExpanded(false);
    setNotaryPaymentModalOpen(false);

    // Refresh user data
    dispatch(fetchUserData(currentSession));
  };

  const handleNotaryPaymentError = (message) => {
    setNotaryError(message);
    setNotaryPaymentModalOpen(false);
  };

  // Notary cancellation modal handlers
  const handleNotaryCancelClick = async () => {
    // Check if already cancelling (should resume instead)
    const isCancelling =
      (currentSession === "subUserSession" &&
        currentUser?.subuser?.notary_user?.cancelling?.started_at) ||
      (currentSession === "userSession" &&
        currentUser?.user?.notary_user?.cancelling?.started_at);

    if (isCancelling) {
      // Resume subscription
      try {
        setNotaryCancelLoading(true);
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/resume-notary-subscription`,
          {
            email: currentUser?.user?.email,
            currentSession: currentSession,
          },
          { withCredentials: true },
        );

        if (resp?.data?.success) {
          setResponseMessage(resp?.data?.message);
          setIsError(false);
        } else {
          setIsError(true);
          setResponseMessage(resp?.data?.message);
        }
      } catch (e) {
        setIsError(true);
        setResponseMessage(
          "An error occurred while resuming the subscription. Please try again.",
        );
      } finally {
        setNotaryCancelLoading(false);
        setTimeout(() => {
          setIsError(false);
          setResponseMessage("");
        }, 3500);
      }
    } else {
      // Open cancellation modal
      setNotaryCancelModalOpen(true);
    }
  };

  const handleNotaryCancelConfirm = async () => {
    try {
      setNotaryCancelLoading(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/delete-notary-user`,
        {
          email: currentUser?.user?.email,
          currentSession: currentSession,
        },
        { withCredentials: true },
      );

      if (resp?.data?.success) {
        setResponseMessage(resp?.data?.message);
        setTimeout(() => {
          setNotaryCancelModalOpen(false);
          setNotaryCancelLoading(false);
        }, 3500);
      } else {
        setIsError(true);
        setResponseMessage(resp?.data?.message);
        setNotaryCancelLoading(false);
      }
    } catch (e) {
      setIsError(true);
      setResponseMessage(
        "An error occurred while canceling the notary. Please try again.",
      );
      setNotaryCancelLoading(false);
    } finally {
      setTimeout(() => {
        setIsError(false);
        setResponseMessage("");
      }, 3500);
    }
  };

  const handleNotaryCancelClose = () => {
    setNotaryCancelModalOpen(false);
  };

  const handleChangeSmsConsent = async () => {
    try {
      setSwitchLoading(true);
      const consent = !smsConsent;
      setSmsConsent(consent);
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/auth/change-sms-consent`,
        {
          currentSession: currentSession,
          smsConsent: consent,
        },
        { withCredentials: true },
      );

      if (currentSession === "userSession") {
        dispatch(changeUserSmsConsent(consent));
      } else {
        dispatch(changeSubUserSmsConsent(consent));
      }
    } catch (error) {
    } finally {
      setSwitchLoading(false);
    }
  };

  const handleLeave = async () => {
    if (leaveInput !== "Yes, I want to cancel my subscription") {
      setErrorTextCancel("Input does not match!");
      setLeaveInput("");
      setCancelSelected("");
    } else if (cancelSelected === "") {
      setErrorTextCancel("Please select a reason!");
    } else {
      setCancelLoading(true);
      const payload = { leaveInput, cancelSelected };
      try {
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/stripe/cancel-subscription`,
          {
            payload,
            currentSession: currentSession,
          },
          { withCredentials: true },
        );
        if (resp.data.success) {
          if (currentSession === "privateProviderSession") {
            await dispatch(fetchPrivateProviderData(currentSession));
          } else if (currentSession === "subUserSession") {
            await dispatch(fetchSubUserData(currentSession));
          } else {
            await dispatch(fetchUserData(currentSession));
          }
          setErrorTextCancel(resp.data.message);
          setCancelLoading(false);
          navigate("/subscription");
        }
      } catch (e) {
        setCancelLoading(false);
        setLeaveInput("");
        setCancelSelected("");
      }
    }
  };

  const passwordFormik = useFormik({
    initialValues: {
      oldPassword: "",
      newPassword: "",
      newPasswordConfirmation: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string()
        .min(8, "password must contain 8 or more characters")
        .max(255)
        .minLowercase(1, "password must contain at least 1 lower case letter")
        .minUppercase(1, "password must contain at least 1 upper case letter")
        .minNumbers(1, "password must contain at least 1 number")
        .minSymbols(1, "password must contain at least 1 special character")
        .required("Password is required"),
      newPassword: Yup.string()
        .min(8, "password must contain 8 or more characters")
        .max(255)
        .minLowercase(1, "password must contain at least 1 lower case letter")
        .minUppercase(1, "password must contain at least 1 upper case letter")
        .minNumbers(1, "password must contain at least 1 number")
        .minSymbols(1, "password must contain at least 1 special character")
        .required("Password is required"),
      newPasswordConfirmation: Yup.string()

        .required("Password confirmation is required")
        .test("passwords-match", "Passwords must match", function (value) {
          return this.parent.newPassword === value;
        }),
    }),
    onSubmit: async (values, helpers) => {
      try {
        setLoading(true);
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/password/changepwd`,
            {
              oldPassword: values.oldPassword,
              newPassword: values.newPassword,
              newPasswordConfirmation: values.newPasswordConfirmation,
              currentSession: currentSession,
            },
            { withCredentials: true },
          );
          if (res.data.success) {
            setErrorTextPassword(res.data.message);
            setLoading(false);
            values.oldPassword = "";
            values.newPassword = "";
            values.newPasswordConfirmation = "";
          }
        } catch (err) {
          setErrorTextPassword(err.response.data.message);
          values.oldPassword = "";
          values.newPassword = "";
          values.newPasswordConfirmation = "";
          setLoading(false);
        }
      } catch (err) {
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: err.message });
        helpers.setSubmitting(false);
        setLoading(false);
      }
    },
  });

  return (
    <>
      {dataLoading ? (
        <>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "100vh",
              backgroundColor: "#fff",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              zIndex: 9999,
            }}
          >
            {tabletSize ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <img
                    style={{
                      width: "10rem",
                      marginLeft: "auto",
                      marginRight: "auto",
                    }}
                    alt="Permit Rockstar Logo"
                    src="/img/logos/logo_main.png"
                  />
                </Box>
                <LottieAnimation />
              </>
            ) : (
              <CircularProgress color="warning" size="10rem" />
            )}
          </Box>
        </>
      ) : (
        <>
          <Box
            sx={{
              mx: largePC ? "auto" : 0,
              maxWidth: "1500px",
              py: 1,
              backgroundColor: "#EEF2F6",
              mt: 1,
            }}
          >
            <Grid
              container
              sx={{
                px: mobileSize ? 1 : tabletSize ? 2 : 3,
              }}
            >
              {currentSession === "privateProviderSession" && (
                <LicenseInfoSection />
              )}
              <Card
                pb={3}
                pt={1}
                px={3}
                mb={4}
                component={Grid}
                sx={{
                  width: tabletSize ? "90vw" : "60vw",
                  maxWidth: "1100px",
                  mx: "auto",
                }}
              >
                <Grid container mt={tabletSize ? 1 : undefined}>
                  <Grid size={{ xs: 12 }} mt={1}>
                    <Typography
                      sx={{ textAlign: "center", mb: mobileSize ? 1 : 5 }}
                      variant="h5"
                    >
                      Change Password
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <form noValidate onSubmit={passwordFormik.handleSubmit}>
                      <Stack spacing={mobileSize ? 1 : 3} mt={4}>
                        <TextField
                          size={mobileSize ? "small" : undefined}
                          error={
                            !!(
                              passwordFormik.touched.oldPassword &&
                              passwordFormik.errors.oldPassword
                            )
                          }
                          helperText={
                            passwordFormik.touched.oldPassword &&
                            passwordFormik.errors.oldPassword
                          }
                          onBlur={passwordFormik.handleBlur}
                          onChange={passwordFormik.handleChange}
                          fullWidth
                          label="Old Password*"
                          name="oldPassword"
                          type={showOldPassword ? "text" : "password"}
                          value={passwordFormik.values.oldPassword}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => {
                                    setShowOldPassword(!showOldPassword);
                                  }}
                                  onMouseDown={(e) => e.preventDefault()} // Prevent focus change on mouse down
                                  onMouseUp={(e) => e.preventDefault()} // Prevent focus change on mouse up
                                  edge="end"
                                >
                                  {showOldPassword ? (
                                    <Visibility />
                                  ) : (
                                    <VisibilityOff />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          size={mobileSize ? "small" : undefined}
                          error={
                            !!(
                              passwordFormik.touched.newPassword &&
                              passwordFormik.errors.newPassword
                            )
                          }
                          helperText={
                            passwordFormik.touched.newPassword &&
                            passwordFormik.errors.newPassword
                          }
                          onBlur={passwordFormik.handleBlur}
                          onChange={passwordFormik.handleChange}
                          fullWidth
                          label="New Password*"
                          name="newPassword"
                          type={showNewPassword ? "text" : "password"}
                          value={passwordFormik.values.newPassword}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => {
                                    setShowNewPassword(!showNewPassword);
                                  }}
                                  onMouseDown={(e) => e.preventDefault()} // Prevent focus change on mouse down
                                  onMouseUp={(e) => e.preventDefault()} // Prevent focus change on mouse up
                                  edge="end"
                                >
                                  {showNewPassword ? (
                                    <Visibility />
                                  ) : (
                                    <VisibilityOff />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          size={mobileSize ? "small" : undefined}
                          error={
                            !!(
                              passwordFormik.touched.newPasswordConfirmation &&
                              passwordFormik.errors.newPasswordConfirmation
                            )
                          }
                          helperText={
                            passwordFormik.touched.newPasswordConfirmation &&
                            passwordFormik.errors.newPasswordConfirmation
                          }
                          onBlur={passwordFormik.handleBlur}
                          onChange={passwordFormik.handleChange}
                          fullWidth
                          label="Confirm Password*"
                          name="newPasswordConfirmation"
                          type="password"
                          value={passwordFormik.values.newPasswordConfirmation}
                        />
                        {passwordFormik.errors.submit && (
                          <Typography
                            color="error"
                            sx={{ mt: 3 }}
                            variant="body2"
                          >
                            {passwordFormik.errors.submit}
                          </Typography>
                        )}
                        <Typography
                          variant="body1"
                          color="error"
                          fontWeight="bold"
                          textAlign="center"
                        >
                          {errorTextPassword}
                        </Typography>

                        <LoadingButton
                          size={mobileSize ? "small" : "large"}
                          loading={loading}
                          type="submit"
                          fullWidth
                          sx={{
                            mt: 2,
                            backgroundColor: "primary",
                          }}
                          variant="contained"
                        >
                          Submit Changes
                        </LoadingButton>
                      </Stack>
                    </form>
                  </Grid>
                </Grid>
              </Card>
              {/* SMS Consent & Notifications */}
              <Card
                pb={3}
                pt={2}
                px={3}
                mb={4}
                sx={{
                  width: tabletSize ? "90vw" : "60vw",
                  maxWidth: "1100px",
                  mx: "auto",
                  textAlign: "center",
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  boxShadow: 2,
                  py: 2,
                  mb: 4,
                }}
              >
                <Typography variant="h5" fontWeight="bold" mb={2}>
                  SMS Consent & Notifications
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Typography variant="body1" fontWeight="bold">
                    {smsConsent ? "Enabled" : "Disabled"}
                  </Typography>
                  {smsConsent ? (
                    <CheckCircleIcon sx={{ color: "green" }} />
                  ) : (
                    <CancelIcon sx={{ color: "red" }} />
                  )}
                </Box>

                <Typography variant="body2" sx={{ mb: 2 }}>
                  Enabling SMS allows us to send **important account
                  notifications** and **2FA security alerts**. You can disable
                  it at any time.
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    {smsConsent
                      ? "Turn Off SMS Notifications"
                      : "Turn On SMS Notifications"}
                  </Typography>
                  <Switch
                    disabled={
                      switchLoading
                        ? true
                        : authType === "smsFa" && smsConsent
                          ? true
                          : process.env.REACT_APP_DISABLE_SMS_FA === "true"
                            ? true
                            : false
                    }
                    checked={smsConsent}
                    onChange={() => {
                      handleChangeSmsConsent();
                    }}
                    color="primary"
                  />
                </Box>
                <Typography fontWeight="bold" color="error" variant="body2">
                  {authType === "smsFa" && smsConsent
                    ? "You must disable SMS 2FA before turning off SMS notifications"
                    : process.env.REACT_APP_DISABLE_SMS_FA === "true"
                      ? "SMS-based 2FA verification is temporarily unavailable"
                      : ""}
                </Typography>
              </Card>

              {/* Timezone Settings */}
              <Card
                pb={3}
                pt={2}
                px={3}
                mb={4}
                sx={{
                  width: tabletSize ? "90vw" : "60vw",
                  maxWidth: "1100px",
                  mx: "auto",
                  textAlign: "center",
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  boxShadow: 2,
                  py: 2,
                  mb: 4,
                }}
              >
                <Typography variant="h5" fontWeight="bold" mb={2}>
                  Timezone Settings
                </Typography>

                <Typography variant="body2" sx={{ mb: 2 }}>
                  Select your timezone for date and time display across Permit
                  Rockstar.
                </Typography>

                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <TimezoneSelect
                    options={timezoneMappings}
                    timezone={timezone}
                    setTimezone={setTimezone}
                  />
                </Box>
              </Card>

              {/* Notary User Setup - Only for main users */}
              {canSeeNotaryUserSetup && (
                <Card
                  pb={3}
                  pt={2}
                  px={3}
                  mb={4}
                  sx={{
                    width: tabletSize ? "90vw" : "60vw",
                    maxWidth: "1100px",
                    mx: "auto",
                    textAlign: "center",
                    border: "1px solid #ddd",
                    borderRadius: 2,
                    boxShadow: 2,
                    py: 2,
                    mb: 4,
                  }}
                >
                  <Typography variant="h5" fontWeight="bold" mb={2}>
                    Notary User Setup
                  </Typography>

                  {isMainUserAlreadyNotary() ? (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 2,
                          mb: 0,
                        }}
                      >
                        <NotaryIcon
                          sx={{
                            color:
                              (currentSession === "subUserSession" &&
                                currentUser?.subuser?.notary_user?.cancelling
                                  ?.started_at) ||
                              (currentSession === "userSession" &&
                                currentUser?.user?.notary_user?.cancelling
                                  ?.started_at)
                                ? "orange"
                                : "green",
                            fontSize: 32,
                          }}
                        />
                        <Typography
                          variant="h6"
                          color={
                            (currentSession === "subUserSession" &&
                              currentUser?.subuser?.notary_user?.cancelling
                                ?.started_at) ||
                            (currentSession === "userSession" &&
                              currentUser?.user?.notary_user?.cancelling
                                ?.started_at)
                              ? "warning"
                              : "green"
                          }
                          fontWeight="bold"
                        >
                          {(currentSession === "subUserSession" &&
                            currentUser?.subuser?.notary_user?.cancelling
                              ?.started_at) ||
                          (currentSession === "userSession" &&
                            currentUser?.user?.notary_user?.cancelling
                              ?.started_at)
                            ? "Your Notary User Account is Scheduled to be Cancelled at:"
                            : "You are already set up as a Notary User"}
                        </Typography>
                      </Box>

                      {/* Notary Details Card */}
                      {convertedNotaryUser &&
                        !(
                          (currentSession === "subUserSession" &&
                            currentUser?.subuser?.notary_user?.cancelling
                              ?.started_at) ||
                          (currentSession === "userSession" &&
                            currentUser?.user?.notary_user?.cancelling
                              ?.started_at)
                        ) && (
                          <Card
                            sx={{
                              maxWidth: 500,
                              margin: "auto",
                              mt: 3,
                              mb: 3,
                              boxShadow: 3,
                              borderRadius: 2,
                            }}
                          >
                            <CardContent>
                              <Grid container spacing={2}>
                                <Grid
                                  size={{ xs: 12 }}
                                  sx={{ textAlign: "center" }}
                                >
                                  <Avatar
                                    sx={{
                                      bgcolor: "primary.main",
                                      width: 80,
                                      height: 80,
                                      margin: "auto",
                                      mb: 2,
                                    }}
                                  >
                                    <NotaryIcon sx={{ fontSize: 40 }} />
                                  </Avatar>
                                  <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                    mb={1}
                                  >
                                    Notary Details
                                  </Typography>
                                </Grid>

                                {convertedNotaryUser?.email && (
                                  <Grid size={{ xs: 12 }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 1,
                                      }}
                                    >
                                      <EmailIcon color="primary" />
                                      <Typography
                                        variant="body2"
                                        color="textSecondary"
                                      >
                                        Email:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                      >
                                        {convertedNotaryUser.email}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}

                                {convertedNotaryUser?.phone && (
                                  <Grid size={{ xs: 12 }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 1,
                                      }}
                                    >
                                      <PhoneIcon color="primary" />
                                      <Typography
                                        variant="body2"
                                        color="textSecondary"
                                      >
                                        Phone:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                      >
                                        {convertedNotaryUser.phone}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}

                                {(convertedNotaryUser?.state ||
                                  convertedNotaryUser?.county) && (
                                  <Grid size={{ xs: 12 }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 1,
                                      }}
                                    >
                                      <LocationOnIcon color="primary" />
                                      <Typography
                                        variant="body2"
                                        color="textSecondary"
                                      >
                                        Location:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                      >
                                        {[
                                          convertedNotaryUser?.county,
                                          convertedNotaryUser?.state,
                                        ]
                                          .filter(Boolean)
                                          .join(", ")}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}

                                {convertedNotaryUser?.commission_number && (
                                  <Grid size={{ xs: 12 }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 1,
                                      }}
                                    >
                                      <BadgeIcon color="primary" />
                                      <Typography
                                        variant="body2"
                                        color="textSecondary"
                                      >
                                        Commission Number:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                      >
                                        {convertedNotaryUser.commission_number}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}

                                {convertedNotaryUser?.notary_type !==
                                  undefined &&
                                  convertedNotaryUser?.notary_type !== null && (
                                    <Grid size={{ xs: 12 }}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                          mb: 1,
                                        }}
                                      >
                                        <NotaryIcon color="primary" />
                                        <Typography
                                          variant="body2"
                                          color="textSecondary"
                                        >
                                          Notary Type:
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          fontWeight="medium"
                                        >
                                          {convertedNotaryUser.notary_type === 0
                                            ? "IPEN Only"
                                            : "IPEN & RON"}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}

                                <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "center",
                                      gap: 1,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    {convertedNotaryUser?.on_site_registered && (
                                      <Chip
                                        icon={<CheckCircleIcon />}
                                        label="PR Registered"
                                        color="success"
                                        size="small"
                                      />
                                    )}
                                    {convertedNotaryUser?.bn_registered && (
                                      <Chip
                                        icon={<CheckCircleIcon />}
                                        label="BN Registered"
                                        color="success"
                                        size="small"
                                      />
                                    )}
                                  </Box>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        )}

                      <Box sx={{ textAlign: "center" }}>
                        {(currentSession === "subUserSession" &&
                          currentUser?.subuser?.notary_user?.cancelling
                            ?.started_at) ||
                        (currentSession === "userSession" &&
                          currentUser?.user?.notary_user?.cancelling
                            ?.started_at) ? (
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            mb={1}
                            color="error"
                          >
                            {(() => {
                              const cancellingData =
                                currentSession === "subUserSession"
                                  ? currentUser?.subuser?.notary_user
                                      ?.cancelling
                                  : currentUser?.user?.notary_user?.cancelling;

                              const hasPendingTasks =
                                cancellingData?.pending_task_ids?.length > 0;

                              if (hasPendingTasks) {
                                return "Cancellation will start once all pending notarizations are completed";
                              } else {
                                const cancellationDate = DateTime.fromISO(
                                  cancellingData?.started_at,
                                ).plus({ hours: 24 });
                                return cancellationDate.toFormat(
                                  "MMM dd, yyyy hh:mm a",
                                );
                              }
                            })()}
                          </Typography>
                        ) : (
                          <></>
                        )}

                        {responseMessage && (
                          <Alert
                            severity={isError ? "error" : "success"}
                            sx={{ mb: 2 }}
                          >
                            {responseMessage}
                          </Alert>
                        )}

                        {!isBranch && (
                          <LoadingButton
                            loading={notaryCancelLoading}
                            variant="contained"
                            color={
                              (currentSession === "subUserSession" &&
                                currentUser?.subuser?.notary_user?.cancelling
                                  ?.started_at) ||
                              (currentSession === "userSession" &&
                                currentUser?.user?.notary_user?.cancelling
                                  ?.started_at)
                                ? "primary"
                                : "error"
                            }
                            onClick={handleNotaryCancelClick}
                          >
                            {(currentSession === "subUserSession" &&
                              currentUser?.subuser?.notary_user?.cancelling
                                ?.started_at) ||
                            (currentSession === "userSession" &&
                              currentUser?.user?.notary_user?.cancelling
                                ?.started_at)
                              ? "Resume Notary Subscription"
                              : "Cancel Notary Subscription"}
                          </LoadingButton>
                        )}
                      </Box>
                    </>
                  ) : !canSetupMainUserAsNotary() ? (
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="body1"
                        color="error"
                        fontWeight="bold"
                        mb={2}
                      >
                        {currentUser?.tier?.title === "Free"
                          ? "Notary User feature is not available for Free plan. Please upgrade to Premium or Rockstar plan."
                          : !canAddNotaryUser()
                            ? `Notary user limit reached! Maximum allowed: ${getNotaryUserLimit()}`
                            : "Unable to setup notary user at this time."}
                      </Typography>
                      {currentUser?.tier?.title === "Free" && (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => navigate("/subscription")}
                          sx={{ mt: 1 }}
                        >
                          Upgrade Plan
                        </Button>
                      )}
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 3 }}>
                        {notarySetupDescription}
                      </Typography>

                      <Accordion
                        expanded={notaryAccordionExpanded}
                        onChange={(event, isExpanded) => {
                          setNotaryAccordionExpanded(isExpanded);
                          if (!isExpanded) {
                            setNotaryError("");
                            setNotarySuccess("");
                            setNotaryFieldErrors({});
                          }
                        }}
                        sx={{ mb: 2 }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{ backgroundColor: "#f5f5f5" }}
                        >
                          <Typography variant="h6" fontWeight="bold">
                            Setup Notary User Account
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid size={{ xs: 12 }}>
                              <Typography
                                color="primary"
                                fontWeight="bold"
                                variant="body1"
                              >
                                Enter your commission number to setup your
                                notary account. We'll search our database to
                                verify your notary credentials.
                              </Typography>
                              <Typography variant="body2" gutterBottom>
                                {notaryNameMatchText}
                              </Typography>
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                              <TextField
                                fullWidth
                                label="Commission Number"
                                variant="filled"
                                value={commissionNumber}
                                onChange={(e) => {
                                  setCommissionNumber(e.target.value);
                                  if (notaryFieldErrors.commissionNumber) {
                                    setNotaryFieldErrors({
                                      ...notaryFieldErrors,
                                      commissionNumber: "",
                                    });
                                  }
                                }}
                                error={!!notaryFieldErrors.commissionNumber}
                                helperText={notaryFieldErrors.commissionNumber}
                                required
                              />
                            </Grid>

                            {notaryError && (
                              <Grid size={{ xs: 12 }}>
                                <Alert severity="error">{notaryError}</Alert>
                              </Grid>
                            )}

                            {notarySuccess && (
                              <Grid size={{ xs: 12 }}>
                                <Alert severity="success">
                                  {notarySuccess}
                                </Alert>
                              </Grid>
                            )}

                            <Grid size={{ xs: 12 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 2,
                                  justifyContent: "flex-end",
                                }}
                              >
                                <Button
                                  variant="outlined"
                                  onClick={() => {
                                    setNotaryAccordionExpanded(false);
                                    setNotaryError("");
                                    setNotarySuccess("");
                                    setNotaryFieldErrors({});
                                  }}
                                  disabled={notaryPaymentModalOpen}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  variant="contained"
                                  onClick={handleNotarySetup}
                                  disabled={
                                    !commissionNumber || notaryPaymentModalOpen
                                  }
                                >
                                  Search & Setup Notary User
                                </Button>
                              </Box>
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  )}
                </Card>
              )}
              <ChangeFA
                qrCodeSrc={qrCodeSrc}
                currentUser={currentUser}
                currentFA={currentFA}
                setCurrentFA={setCurrentFA}
                imageList={imageList}
                setImageList={setImageList}
                currentImg={currentImg}
                setCurrentImg={setCurrentImg}
                imgWidth={imgWidth}
                setImgWidth={setImgWidth}
                mobileSize={mobileSize}
                tabletSize={tabletSize}
                smsConsent={smsConsent}
              />
              {canSeeCancelSubscription && (
                <Box sx={{ mx: "auto" }}>
                  <>
                    {/* REMOVE SUBSCRIPTION */}
                    {currentUser &&
                      (currentUser.user.expired ||
                      currentUser.user.cancellation_time ? (
                        <Card
                          py={3}
                          px={mobileSize ? 1 : 3}
                          mt={0}
                          component={Grid}
                          sx={{
                            visibility: "hidden",
                            width: tabletSize ? "90vw" : "60vw",
                            maxWidth: "1100px",
                            mx: "auto",
                          }}
                        ></Card>
                      ) : (
                        <>
                          <Typography
                            variant="h4"
                            color="error"
                            textAlign="center"
                            mt={4}
                          >
                            DANGER ZONE
                          </Typography>
                          <div
                            style={{
                              border: "4px solid #F01329",
                              marginTop: "0.2rem",
                            }}
                          />
                          <Card
                            py={3}
                            px={mobileSize ? 1 : 3}
                            mt={0}
                            component={Grid}
                            sx={{
                              width: tabletSize ? "90vw" : "60vw",
                              maxWidth: "1100px",
                              mx: "auto",
                            }}
                          >
                            <Grid container>
                              <Grid size={{ sm: 12 }} mb={mobileSize ? 0 : 3}>
                                <Typography
                                  sx={{
                                    textAlign: "center",
                                    mb: mobileSize ? 1 : 2,
                                  }}
                                  variant="h5"
                                >
                                  Cancel Subscription
                                </Typography>
                                <Typography
                                  color="error"
                                  sx={{
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    mb: 3,
                                  }}
                                  variant={mobileSize ? "body2" : "body1"}
                                >
                                  We encourage you to try out our different
                                  packages before making a decision.
                                </Typography>
                              </Grid>
                              <Grid container spacing={mobileSize ? 1 : 10}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <Stack>
                                    <Typography
                                      sx={{
                                        textAlign: "center",
                                        fontWeight: "bold",
                                        mb: 1,
                                      }}
                                      variant="body1"
                                    >
                                      Are you sure to cancel subscription?
                                    </Typography>
                                    <Typography
                                      sx={{
                                        textAlign: "center",
                                        mb: mobileSize ? 1 : 3,
                                      }}
                                      variant="body2"
                                    >
                                      Type in "Yes, I want to cancel my
                                      subscription" to cancel subscription!
                                    </Typography>
                                    <TextField
                                      size={mobileSize ? "small" : undefined}
                                      onChange={(e) => {
                                        setLeaveInput(e.target.value);
                                      }}
                                      fullWidth
                                      label="Please Enter Text"
                                      name="leaveInput"
                                      value={leaveInput}
                                    />
                                  </Stack>
                                </Grid>
                                <Grid
                                  size={{ xs: 12, sm: 6 }}
                                  mt={mobileSize ? 1 : 0}
                                >
                                  <Typography
                                    sx={{
                                      textAlign: "center",
                                      fontWeight: "bold",
                                      mb: mobileSize ? 0 : 1,
                                    }}
                                    variant="body1"
                                  >
                                    Please select reason!
                                  </Typography>
                                  <FormControl fullWidth variant="filled">
                                    <InputLabel>Select</InputLabel>
                                    <Select
                                      size={mobileSize ? "small" : undefined}
                                      label="Select"
                                      value={cancelSelected}
                                      onChange={(e) =>
                                        setCancelSelected(e.target.value)
                                      }
                                    >
                                      {leavingReasons?.cancel_subscription.map(
                                        function (item, i) {
                                          return (
                                            <MenuItem key={i} value={item}>
                                              {item}
                                            </MenuItem>
                                          );
                                        },
                                      )}
                                    </Select>
                                  </FormControl>
                                </Grid>
                              </Grid>
                              <Grid container>
                                <Grid size={{ xs: 12 }} mt={mobileSize ? 0 : 5}>
                                  <Stack>
                                    <Typography
                                      minHeight={mobileSize ? "0rem" : "1.5rem"}
                                      variant="body1"
                                      color="#F2CD00"
                                      fontWeight="bold"
                                      textAlign="center"
                                    >
                                      {errorTextCancel}
                                    </Typography>
                                    <LoadingButton
                                      loading={cancelLoading}
                                      color="error"
                                      onClick={handleLeave}
                                      fullWidth
                                      size={mobileSize ? "small" : "large"}
                                      sx={{
                                        mt: 3,
                                      }}
                                      variant="contained"
                                    >
                                      CANCEL SUBSCRIPTION
                                    </LoadingButton>
                                  </Stack>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Card>
                        </>
                      ))}
                  </>
                </Box>
              )}
            </Grid>
          </Box>
        </>
      )}

      {/* Notary User Payment Modal */}
      <NotaryUserPaymentModal
        open={notaryPaymentModalOpen}
        onClose={() => setNotaryPaymentModalOpen(false)}
        notaryUserData={notaryUserData}
        currentUser={currentUser}
        currentSession={currentSession}
        onPaymentSuccess={handleNotaryPaymentSuccess}
        onPaymentError={handleNotaryPaymentError}
      />

      <NotaryCancelModal
        handleNotaryCancelConfirm={handleNotaryCancelConfirm}
        notaryCancelModalOpen={notaryCancelModalOpen}
        handleNotaryCancelClose={handleNotaryCancelClose}
        notaryCancelLoading={notaryCancelLoading}
        responseMessage={responseMessage}
        isError={isError}
      />
    </>
  );
};

export default ProfileSettings;
